import { Component, OnInit, Output, Input, EventEmitter, OnDestroy } from "@angular/core";
import { PatientsService } from "../patients.service";
import { LoginService } from "../login.service";
import { AppointmentsService } from "../appointments.service";
import { LanguageService } from "../language.service";
import { DatePipe } from "@angular/common";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

@Component({
    selector: "app-appointment-list",
    templateUrl: "./appointment-list.component.html",
    styleUrls: ["./appointment-list.component.scss"],
    standalone: false
})
export class AppointmentListComponent implements OnInit {
  @Output() pageChange = new EventEmitter<string>();
  appointments = [];
  allAppointments = [];
  view = "visits"; /**or messages */
  filterFromDate: Date | null = new Date();
  filterToDate: Date | null = new Date();

  filteredAppointments = [];
  filteredVisits = [];
  pageDirection: string = "ltr";

  constructor(
    public patientService: PatientsService,
    public loginService: LoginService,
    public appointmentService: AppointmentsService,
    public languageService: LanguageService,
    private datePipe: DatePipe
  ) {}

  async ngOnInit() {
    this.pageDirection = this.languageService.getPageDirection();
    this.switchViewMode(this.view);
    const today = new Date();
    this.filterFromDate = today;
    this.filterToDate = today;

    this.allAppointments = await this.appointmentService.getNotApprovedAppointments();
    this.filteredAppointments = [...this.allAppointments];
  }

  async getAppointmentsMadeByPatient(status) {
    this.appointments = await this.appointmentService.getAppointmentsMadeByPatient(status);
    this.filteredVisits = [...this.appointments];
  }

  async changeStatus(status, appointmentId) {
    const result = await this.appointmentService.changeStatus(status, appointmentId);
    if (result["status"] == "ok") {
      this.getAppointmentsMadeByPatient(["waiting", "pending", "approved"]);
    }
  }

  async onStatusChange(appointment: any) {
    const result = await this.appointmentService.changeStatus(appointment.Status, appointment.AutoIndex);
    if (result["status"] == "ok") {
      this.getAppointmentsMadeByPatient(["waiting", "pending", "approved"]);
    }
  }

  switchViewMode(mode) {
    switch (mode) {
      case "visits":
        this.getAppointmentsMadeByPatient(["waiting", "pending", "approved"]);
        break;
      default:
        break;
    }
  }

  setPage(appointment) {
    this.pageChange.emit(appointment);
  }

  sortByCreationTime(order: "asc" | "desc") {
    this.filteredVisits.sort((a, b) => {
      const timeDiff = new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
      if (timeDiff !== 0) return order === "asc" ? timeDiff : -timeDiff;

      // Secondary sort fallback (by AutoIndex or ID)
      return order === "asc" ? a.AutoIndex - b.AutoIndex : b.AutoIndex - a.AutoIndex;
    });
  }

  sortByCreationTimeAllAppts(order: "asc" | "desc") {
    this.filteredAppointments.sort((a, b) => {
      const timeDiff = new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
      if (timeDiff !== 0) return order === "asc" ? timeDiff : -timeDiff;

      // Secondary sort fallback (by AutoIndex or ID)
      return order === "asc" ? a.AutoIndex - b.AutoIndex : b.AutoIndex - a.AutoIndex;
    });
  }

  applyDateFilter() {
    if (!this.filterFromDate && !this.filterToDate) {
      this.filteredAppointments = [...this.allAppointments];
      return;
    }

    const from = this.filterFromDate ? new Date(this.filterFromDate).setHours(0, 0, 0, 0) : null;
    const to = this.filterToDate ? new Date(this.filterToDate).setHours(23, 59, 59, 999) : null;

    this.filteredAppointments = this.allAppointments.filter((appt) => {
      const created = new Date(appt.createdDate).getTime();
      const isAfterFrom = !from || created >= from;
      const isBeforeTo = !to || created <= to;
      return isAfterFrom && isBeforeTo;
    });
  }

  clearDateFilter() {
    this.filterFromDate = null;
    this.filterToDate = null;
    this.filteredAppointments = [...this.allAppointments];
  }

  sortByStatus(order: "asc" | "desc") {
    const statusOrder = ["waiting", "pending", "approved"];

    this.filteredVisits = [...this.filteredVisits].sort((a, b) => {
      const indexA = statusOrder.indexOf(a.Status);
      const indexB = statusOrder.indexOf(b.Status);

      if (order === "asc") {
        return indexA - indexB;
      } else {
        return indexB - indexA;
      }
    });
  }

  applyVisitsDateFilter() {
    if (!this.filterFromDate && !this.filterToDate) {
      this.filteredVisits = [...this.appointments];
      return;
    }

    const from = this.filterFromDate ? new Date(this.filterFromDate).setHours(0, 0, 0, 0) : null;
    const to = this.filterToDate ? new Date(this.filterToDate).setHours(23, 59, 59, 999) : null;

    this.filteredVisits = this.appointments.filter((appt) => {
      const created = new Date(appt.createdDate).getTime();
      const isAfterFrom = !from || created >= from;
      const isBeforeTo = !to || created <= to;
      return isAfterFrom && isBeforeTo;
    });
  }

  clearVisitsDateFilter() {
    this.filterFromDate = null;
    this.filterToDate = null;
    this.filteredVisits = [...this.appointments];
  }

  private downloadWorkbook(wb: XLSX.WorkBook, fileName: string) {
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, fileName);
  }

  exportExcel() {
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    let data: any[];
    let sheetName: string;

    switch (this.view) {
      case "visits":
        sheetName = "SelfScheduling";
        data = this.filteredVisits.map((a, i) => ({
          "#": i + 1,
          "Creation Time": this.datePipe.transform(a.createdDate, "dd.MM.yyyy HH:mm"),
          "Exam Name": a.isCustom ? a.examType : a.examName,
          "Patient ID": a.ID,
          "Patient Name": `${a.First} ${a.Last}`,
          Mobile: a.Mobile,
          ...(this.loginService.premissions?.ApptsByRoom
            ? { Room: this.loginService.getRoomByRID(a.RID) }
            : { Doctor: a.docName }),
          Status: a.Status,
        }));
        break;

      case "notConfirmed":
        sheetName = "UnapprovedArrivals";
        data = this.filteredAppointments.map((a, i) => ({
          "#": i + 1,
          "Creation Time": this.datePipe.transform(a.createdDate, "dd.MM.yyyy HH:mm"),
          "Exam Name": a.isCustom ? a.examType : a.examName,
          "Patient ID": a.ID,
          "Patient Name": `${a.First} ${a.Last}`,
          Mobile: a.Mobile,
          ...(this.loginService.premissions?.ApptsByRoom
            ? { Room: this.loginService.getRoomByRID(a.RID) }
            : { Doctor: a.docName }),
        }));
        break;

      default:
        return;
    }

    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    this.downloadWorkbook(wb, `${sheetName}_${new Date().getTime()}.xlsx`);
  }
}
